/**
 * Copyright (c) 2026, CheckPlus Limited
 */
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from "react";
import { withRouter } from "react-router-dom"; // Import withRouter HOC
import PropTypes from "prop-types";
import "./OrgPlanBuilder.css";
// Assuming a common header/logo component exists or create a simple one
// import OrgHeader from '../shared/OrgHeader'; // Example path

// --- Configuration ---
const BASE_PLANS = {
  Free: {
    name: "Free Tier",
    monthlyPrice: 0,
    annualPrice: 0, // No discount for free tier
    includedMembers: 1,
    includedCampaigns: 0,
  },
  Blue: {
    name: "Blue",
    monthlyPrice: 550,
    annualPrice: 458, // Based on $5,500/year (~16.7% discount)
    includedMembers: 1,
    includedCampaigns: 3,
  },
  Plus: {
    name: "Plus",
    monthlyPrice: 950,
    annualPrice: 792, // Based on $9,500/year (~16.6% discount)
    includedMembers: 3,
    includedCampaigns: 5,
  },
  Premium: {
    name: "Premium",
    monthlyPrice: 1500,
    annualPrice: 1250, // Based on $15,000/year (~16.7% discount)
    includedMembers: 5,
    includedCampaigns: 99, // Use 99 as proxy for Infinity
  },
};

// Helper function to get price based on billing cycle
const getPlanPrice = (plan, billingCycle) => {
  if (!plan) return 0;
  return billingCycle === "annual" ? plan.annualPrice : plan.monthlyPrice;
};

const FEATURE_COSTS = {
  teamMember: { free: 148, paid: 48 },
  paymentMethod: { free: 248, paid: 238 },
  campaign: { free: 248, paid: 78 }, // Note: campaign cost is defined but not used in calculation per new rules
};

const PAYMENT_METHODS = [
  { id: "stripe", name: "Stripe", group: null }, // Stripe is always included
  { id: "hsbc_payme", name: "HSBC PayMe", group: "payme" },
  { id: "tapngo", name: "Tap&Go", group: null, hidden: true }, // Still in development
  { id: "paypal", name: "PayPal", group: null, hidden: true }, // Still in development
  { id: "apple_pay", name: "Apple Pay", group: "mobile" },
  { id: "google_pay", name: "Google Pay", group: "mobile" },
  { id: "alipay", name: "Alipay", group: "china", hidden: true }, // Chinese payment methods still in development
  { id: "wechat_pay", name: "WeChat Pay", group: "china", hidden: true },
];

// Define payment method groups
const PAYMENT_METHOD_GROUPS = {
  mobile: {
    id: "mobile",
    name: "Mobile Payment Solutions",
    methods: ["apple_pay", "google_pay"],
  },
  payme: {
    id: "payme",
    name: "HSBC PayMe for Business",
    methods: ["hsbc_payme"],
  },
  china: {
    id: "china",
    name: "Chinese Payment Solutions",
    methods: ["alipay", "wechat_pay"],
    hidden: true, // Hide Chinese payment solutions as they're still in development
  },
};

// Define plan-specific payment method rules
const PLAN_PAYMENT_RULES = {
  Free: {
    includedMethods: ["stripe"],
    allowedGroups: [],
  },
  Blue: {
    includedMethods: ["stripe"],
    allowedGroups: [],
  },
  Plus: {
    includedMethods: ["stripe"],
    allowedGroups: ["mobile", "payme"],
    mutuallyExclusive: false, // Allow selecting both groups (with additional cost)
  },
  Premium: {
    // All payment methods are included in Premium
    includedMethods: ["stripe", "hsbc_payme", "apple_pay", "google_pay"],
    allowedGroups: ["mobile", "payme"],
    mutuallyExclusive: false, // Can select all methods
  },
};

const MIN_MEMBERS = 1;
const MAX_MEMBERS = 10; // Max selectable members
const MIN_CAMPAIGNS = 0;
const MAX_CAMPAIGNS = 20; // Max selectable campaigns (increased to allow for more campaigns)
// --- End Configuration ---

// Receive history from props injected by withRouter
const OrgPlanBuilderComponent = ({ languageObj, history }) => {
  // const history = useHistory(); // Remove hook call

  // --- State ---
  const [selectedBasePlanId, setSelectedBasePlanId] = useState("Free");
  const [billingCycle, setBillingCycle] = useState("monthly"); // 'monthly' or 'annual'
  const [teamMembers, setTeamMembers] = useState(MIN_MEMBERS);
  // Ensure 'stripe' is always included and cannot be removed by user actions affecting the state directly
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState([
    "stripe",
  ]);
  const [additionalCampaigns, setAdditionalCampaigns] = useState(MIN_CAMPAIGNS); // Represents the number selected by the user
  const [totalMonthlyCost, setTotalMonthlyCost] = useState(0);
  const [totalAnnualCost, setTotalAnnualCost] = useState(0); // For displaying annual total when annual billing is selected
  const [upgradeSuggestion, setUpgradeSuggestion] = useState(null);
  const [affordablePlanRecommendation, setAffordablePlanRecommendation] =
    useState(null); // State for affordable plan suggestion
  const [showPlanComparison, setShowPlanComparison] = useState(false); // State for comparison modal visibility
  // const [basePlanPrices, setBasePlanPrices] = useState({}); // If fetching from API later
  // const [isLoading, setIsLoading] = useState(false); // For API calls

  // --- Collapsible Section State ---
  const [expandedSections, setExpandedSections] = useState({
    basePlan: true, // Start with only the first section expanded
    billingCycle: false,
    teamMembers: false,
    paymentMethods: false,
    campaigns: false,
  });

  const [completedSections, setCompletedSections] = useState({
    basePlan: false,
    billingCycle: false,
    teamMembers: false,
    paymentMethods: false,
    campaigns: false,
  });

  const [activeSection, setActiveSection] = useState("basePlan");

  // --- Pricing Calculation ---
  const calculateTotalCost = useCallback(() => {
    const basePlan = BASE_PLANS[selectedBasePlanId];
    if (!basePlan) return 0; // Should not happen, but safe guard

    const planPrice = getPlanPrice(basePlan, billingCycle);
    const isPaidBasePlan = planPrice > 0;
    const costs = FEATURE_COSTS;

    let calculatedCost = planPrice;
    const includedMembers = basePlan.includedMembers || 0;

    // Team Members Cost (charge only for members exceeding the included amount)
    const chargeableMembers = Math.max(0, teamMembers - includedMembers);
    const memberCostPerUser = isPaidBasePlan
      ? costs.teamMember.paid
      : costs.teamMember.free;
    calculatedCost += chargeableMembers * memberCostPerUser;

    // Payment Methods Cost - count by group
    const additionalMethods = selectedPaymentMethods.filter(
      (m) => m !== "stripe",
    );
    const selectedGroups = [];
    const individualMethods = [];

    // Categorize selected methods into groups and individuals
    additionalMethods.forEach((methodId) => {
      const method = PAYMENT_METHODS.find((m) => m.id === methodId);
      if (method && method.group) {
        // Only add the group if it's not already in the array
        if (!selectedGroups.includes(method.group)) {
          selectedGroups.push(method.group);
        }
      } else if (method && !method.hidden) {
        individualMethods.push(methodId);
      }
    });

    // Calculate cost: count each group as 1 + individual methods
    let totalMethodCount = selectedGroups.length + individualMethods.length;
    const methodCost = isPaidBasePlan
      ? costs.paymentMethod.paid
      : costs.paymentMethod.free;

    // For Plus plan, one payment method group is included
    if (selectedBasePlanId === "Plus" && selectedGroups.length > 0) {
      // Subtract one from the count to account for the included group
      totalMethodCount = Math.max(0, totalMethodCount - 1);
    }

    calculatedCost += totalMethodCount * methodCost;

    // Campaigns Cost - charge for campaigns exceeding the included amount
    if (selectedBasePlanId !== "Premium") {
      // Premium has unlimited campaigns
      const includedCampaigns = basePlan.includedCampaigns || 0;
      const additionalCampaignsCount = Math.max(
        0,
        additionalCampaigns - includedCampaigns,
      );
      const campaignCost = isPaidBasePlan
        ? costs.campaign.paid
        : costs.campaign.free;
      calculatedCost += additionalCampaignsCount * campaignCost;
    }

    return calculatedCost;
  }, [
    selectedBasePlanId,
    billingCycle,
    teamMembers,
    selectedPaymentMethods,
    additionalCampaigns,
  ]); // Added additionalCampaigns dependency

  // --- Upgrade Suggestion Logic ---
  const calculateUpgradeSuggestion = useCallback(
    (currentCost) => {
      let bestSuggestion = null;
      let maxSavings = 0;

      // Iterate through paid plans to find the one with maximum savings
      Object.entries(BASE_PLANS).forEach(([planId, plan]) => {
        const planPrice = getPlanPrice(plan, billingCycle);
        const currentPlanPrice = getPlanPrice(
          BASE_PLANS[selectedBasePlanId],
          billingCycle,
        );

        // Only suggest plans more expensive than the current base plan
        if (planPrice > currentPlanPrice && currentCost > planPrice) {
          const currentSavings = currentCost - planPrice;
          if (currentSavings > maxSavings) {
            maxSavings = currentSavings;

            // Calculate additional details for the suggestion
            const currentPlan = BASE_PLANS[selectedBasePlanId];

            // Calculate member savings
            const currentIncludedMembers = currentPlan.includedMembers || 0;
            const suggestedIncludedMembers = plan.includedMembers || 0;
            const additionalIncludedMembers = Math.max(
              0,
              suggestedIncludedMembers - currentIncludedMembers,
            );
            const memberCostPerUser = isPaidBase
              ? FEATURE_COSTS.teamMember.paid
              : FEATURE_COSTS.teamMember.free;
            const memberSavings =
              Math.min(chargeableMembers, additionalIncludedMembers) *
              memberCostPerUser;

            // Calculate campaign savings
            const currentIncludedCampaigns = currentPlan.includedCampaigns || 0;
            const suggestedIncludedCampaigns = plan.includedCampaigns || 0;
            const additionalIncludedCampaigns = Math.max(
              0,
              suggestedIncludedCampaigns - currentIncludedCampaigns,
            );
            const campaignCostPerUnit = isPaidBase
              ? FEATURE_COSTS.campaign.paid
              : FEATURE_COSTS.campaign.free;

            // Calculate how many additional campaigns the user would need
            const additionalCampaignsNeeded = Math.max(
              0,
              additionalCampaigns - currentIncludedCampaigns,
            );

            // Calculate how many of those would be covered by the new plan
            const campaignsCoveredByUpgrade = Math.min(
              additionalCampaignsNeeded,
              additionalIncludedCampaigns,
            );
            const campaignSavings =
              campaignsCoveredByUpgrade * campaignCostPerUnit;

            // Calculate annual savings
            const annualSavings = currentSavings * 12;

            // Calculate savings percentage
            const savingsPercentage = Math.round(
              (currentSavings / currentCost) * 100,
            );

            bestSuggestion = {
              planId: planId, // Store the ID for the click handler
              planName: plan.name,
              planPrice: planPrice,
              savings: currentSavings,
              annualSavings: annualSavings,
              savingsPercentage: savingsPercentage,
              memberSavings: memberSavings,
              campaignSavings: campaignSavings,
              additionalIncludedMembers: additionalIncludedMembers,
              additionalIncludedCampaigns: additionalCampaignsNeeded,
              campaignsCoveredByUpgrade: campaignsCoveredByUpgrade,
              isPremium: planId === "Premium", // Flag if this is the Premium plan with unlimited campaigns
            };
          }
        }
      });

      return bestSuggestion; // Return the plan with the most savings, or null
    },
    [
      selectedBasePlanId,
      billingCycle,
      isPaidBase,
      chargeableMembers,
      additionalCampaigns,
    ],
  ); // Added additionalCampaigns dependency

  // --- Affordable Plan Calculation ---
  const calculateMostAffordablePlan = useCallback(() => {
    // Store costs for each plan based on current selections
    const planCosts = {};
    // Track which plans are actually compatible with user selections
    const compatiblePlans = {};

    // For each plan, calculate total cost with current selections
    Object.entries(BASE_PLANS).forEach(([planId, plan]) => {
      // Get base plan price
      // Get base plan price
      const basePlanPrice = getPlanPrice(plan, billingCycle);
      const isPaidPlan = basePlanPrice > 0;

      // Check payment method compatibility
      const planRules = PLAN_PAYMENT_RULES[planId];
      const includedMethods = planRules
        ? planRules.includedMethods
        : ["stripe"];
      const allowedGroups = planRules ? planRules.allowedGroups : [];

      // Track if this plan is compatible with selected payment methods
      let isPaymentMethodCompatible = true;

      // Check if any selected payment method is not supported by this plan
      const selectedMethodGroups = [];

      // Group selected methods and check compatibility
      selectedPaymentMethods.forEach((methodId) => {
        if (includedMethods.includes(methodId)) return; // Already included

        const method = PAYMENT_METHODS.find((m) => m.id === methodId);
        if (!method || method.hidden) return; // Skip if not found or hidden

        if (method.group) {
          if (!selectedMethodGroups.includes(method.group)) {
            selectedMethodGroups.push(method.group);
          }

          // If method has a group but plan doesn't support this group, mark as incompatible
          if (!allowedGroups.includes(method.group)) {
            isPaymentMethodCompatible = false;
          }
        } else {
          // If it's an individual method (not Stripe) and the plan is Free or Blue, it's incompatible
          if (planId === "Free" || planId === "Blue") {
            isPaymentMethodCompatible = false;
          }
        }
      });

      // Calculate costs only if plan is compatible
      if (isPaymentMethodCompatible) {
        // Calculate team member costs
        const includedMembers = plan.includedMembers || 0;
        const extraMembers = Math.max(0, teamMembers - includedMembers);
        const memberCost =
          extraMembers *
          (isPaidPlan
            ? FEATURE_COSTS.teamMember.paid
            : FEATURE_COSTS.teamMember.free);

        // Calculate payment method costs
        let methodCost = 0;
        const selectedGroups = [];
        const individualMethods = [];

        // Group selected methods
        selectedPaymentMethods.forEach((methodId) => {
          if (includedMethods.includes(methodId)) return; // Skip included methods

          const method = PAYMENT_METHODS.find((m) => m.id === methodId);
          if (!method || method.hidden) return; // Skip if not found or hidden

          if (method.group && allowedGroups.includes(method.group)) {
            if (!selectedGroups.includes(method.group)) {
              selectedGroups.push(method.group);
            }
          } else if (!method.group) {
            // Only count individual methods
            individualMethods.push(methodId);
          }
        });

        // Calculate method costs (with special handling for Plus plan)
        let chargeMethods = selectedGroups.length + individualMethods.length;
        if (planId === "Plus" && selectedGroups.length > 0) {
          chargeMethods = Math.max(0, chargeMethods - 1); // First group is free
        }

        methodCost =
          chargeMethods *
          (isPaidPlan
            ? FEATURE_COSTS.paymentMethod.paid
            : FEATURE_COSTS.paymentMethod.free);

        // Calculate campaign costs
        let campaignCost = 0;
        if (planId !== "Premium") {
          // Premium has unlimited campaigns
          const includedCampaigns = plan.includedCampaigns || 0;
          const extraCampaigns = Math.max(
            0,
            additionalCampaigns - includedCampaigns,
          );
          campaignCost =
            extraCampaigns *
            (isPaidPlan
              ? FEATURE_COSTS.campaign.paid
              : FEATURE_COSTS.campaign.free);
        }

        // Calculate total cost
        const totalCost =
          basePlanPrice + memberCost + methodCost + campaignCost;

        // Store in our results object
        planCosts[planId] = {
          planId,
          planName: plan.name,
          baseCost: basePlanPrice,
          memberCost,
          methodCost,
          campaignCost,
          totalCost,
          savings: 0, // Will calculate after we have all costs
        };

        // Mark as compatible
        compatiblePlans[planId] = true;
      }
    });

    // Find the current plan's cost (if it's compatible)
    const currentPlanCost = compatiblePlans[selectedBasePlanId]
      ? planCosts[selectedBasePlanId].totalCost
      : Infinity; // Use Infinity if current plan is incompatible

    // Find the cheapest COMPATIBLE plan
    let cheapestPlanId = null; // Start with null
    let minCost = Infinity; // Start with Infinity

    Object.entries(planCosts).forEach(([planId, planData]) => {
      // Only consider compatible plans
      if (compatiblePlans[planId]) {
        if (planData.totalCost < minCost) {
          cheapestPlanId = planId;
          minCost = planData.totalCost;
        }

        // Calculate potential savings compared to current selection (only if current is compatible)
        if (currentPlanCost !== Infinity) {
          planData.savings = currentPlanCost - planData.totalCost;
        } else {
          planData.savings = 0; // No savings if current plan is incompatible
        }
      }
    });

    // If no compatible plan found, or the cheapest is the current one, don't recommend
    if (!cheapestPlanId || cheapestPlanId === selectedBasePlanId) {
      return null; // Return null to indicate no recommendation
    }

    // Ensure potential savings are positive
    const potentialSavings =
      planCosts[cheapestPlanId].savings > 0
        ? planCosts[cheapestPlanId].savings
        : 0;

    // Only recommend if there are actual savings
    if (potentialSavings <= 0) {
      return null;
    }

    // Return the recommendation with details
    return {
      recommendedPlanId: cheapestPlanId,
      allPlanCosts: planCosts,
      currentPlanId: selectedBasePlanId,
      potentialSavings: potentialSavings,
    };
  }, [
    selectedBasePlanId,
    billingCycle,
    teamMembers,
    selectedPaymentMethods,
    additionalCampaigns,
  ]);

  // --- Effects ---
  useEffect(() => {
    // Recalculate cost whenever selections change
    const newTotalCost = calculateTotalCost();
    setTotalMonthlyCost(newTotalCost);

    // Calculate annual cost for display purposes
    if (billingCycle === "annual") {
      setTotalAnnualCost(newTotalCost * 12);
    }

    // Update upgrade suggestion
    const suggestion = calculateUpgradeSuggestion(newTotalCost);
    setUpgradeSuggestion(suggestion);

    // Add affordable plan recommendation
    const recommendation = calculateMostAffordablePlan();
    setAffordablePlanRecommendation(recommendation);
  }, [
    selectedBasePlanId,
    billingCycle,
    teamMembers,
    selectedPaymentMethods,
    calculateTotalCost,
    calculateUpgradeSuggestion,
    calculateMostAffordablePlan,
    additionalCampaigns,
  ]); // Added missing dependency & new function

  // --- Collapsible Section Helpers ---
  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
    setActiveSection(sectionId);
  };

  // Mark section as completed and expand next section
  const completeSection = (currentSection, nextSection) => {
    setCompletedSections((prev) => ({
      ...prev,
      [currentSection]: true,
    }));

    if (nextSection) {
      setExpandedSections((prev) => ({
        ...prev,
        [currentSection]: false, // Collapse current section
        [nextSection]: true, // Expand next section
      }));
      setActiveSection(nextSection);
    } else {
      // If it's the last section, just mark complete and collapse
      setExpandedSections((prev) => ({
        ...prev,
        [currentSection]: false,
      }));
      setActiveSection(null); // Or maybe 'summary'?
    }
  };

  // Jump to a specific section from progress bar
  const jumpToSection = (sectionId) => {
    setExpandedSections((prev) => {
      const newState = { ...prev };
      // Expand the clicked section
      newState[sectionId] = true;
      // Optionally collapse others or keep them as they are
      return newState;
    });
    setActiveSection(sectionId);
  };

  // --- Event Handlers ---
  const handleBasePlanChange = (event) => {
    const newPlanId = event.target.value;
    setSelectedBasePlanId(newPlanId);
    // No longer reset campaigns to the plan limit - allow users to select more campaigns than included

    // Reset members if new plan includes more than currently selected? Optional.
    // const newPlanIncludedMembers = BASE_PLANS[newPlanId] ? BASE_PLANS[newPlanId].includedMembers || 0 : 0;
    // if (teamMembers < newPlanIncludedMembers) {
    //     setTeamMembers(newPlanIncludedMembers);
    // }
  };

  const handleTeamMemberChange = (event) => {
    setTeamMembers(Number(event.target.value));
  };

  // Helper function to check if a method is included in the current plan
  const isMethodIncludedInPlan = (methodId) => {
    if (selectedBasePlanId === "Premium") {
      return PLAN_PAYMENT_RULES.Premium.includedMethods.includes(methodId);
    }
    return methodId === "stripe"; // Stripe is always included
  };

  // Helper function to check if a method belongs to a group
  const getMethodGroup = (methodId) => {
    const method = PAYMENT_METHODS.find((m) => m.id === methodId);
    return method ? method.group : null;
  };

  // Helper function to get all methods in a group
  const getMethodsInGroup = (groupId) => {
    return PAYMENT_METHOD_GROUPS[groupId]
      ? PAYMENT_METHOD_GROUPS[groupId].methods || []
      : [];
  };

  // Helper function to handle payment method group selection
  const handlePaymentMethodGroupChange = (groupId, checked) => {
    const group = PAYMENT_METHOD_GROUPS[groupId];
    if (!group) return;

    if (checked) {
      // Normal behavior - add all methods in the group
      setSelectedPaymentMethods((prev) => {
        const newMethods = [...prev];
        group.methods.forEach((methodId) => {
          if (!newMethods.includes(methodId)) {
            newMethods.push(methodId);
          }
        });
        return newMethods;
      });
    } else {
      // Remove all methods in the group
      setSelectedPaymentMethods((prev) =>
        prev.filter((id) => !group.methods.includes(id)),
      );
    }
  };

  // Updated payment method change handler
  const handlePaymentMethodChange = (event) => {
    const { value, checked } = event.target;

    // Prevent unchecking Stripe or any included methods in Premium plan
    if (isMethodIncludedInPlan(value) && !checked) {
      return;
    }

    // Get the current method's group (if any)
    const methodGroup = getMethodGroup(value);

    // Normal behavior for all cases
    setSelectedPaymentMethods((prev) =>
      checked
        ? [...prev, value]
        : prev.filter((methodId) => methodId !== value),
    );
  };

  const handleCampaignChange = (event) => {
    setAdditionalCampaigns(Number(event.target.value));
  };

  // Handler for clicking the suggested plan button
  const handleSelectSuggestedPlan = (suggestedPlanId) => {
    if (suggestedPlanId && BASE_PLANS[suggestedPlanId]) {
      setSelectedBasePlanId(suggestedPlanId);
      // Reset campaigns based on the new suggested plan's limit
      const newPlanMaxCampaigns = BASE_PLANS[suggestedPlanId]
        ? BASE_PLANS[suggestedPlanId].includedCampaigns || 0
        : 0;
      if (
        additionalCampaigns > newPlanMaxCampaigns &&
        suggestedPlanId !== "Premium"
      ) {
        setAdditionalCampaigns(newPlanMaxCampaigns);
      }
    }
  };

  // Handler for billing cycle change
  const handleBillingCycleChange = (cycle) => {
    setBillingCycle(cycle);
  };

  const handleProceed = () => {
    // setIsLoading(true); // If API call needed
    const currentPlan = BASE_PLANS[selectedBasePlanId];
    const campaignsToSubmit =
      selectedBasePlanId === "Premium" ? "unlimited" : additionalCampaigns; // Use the actual number selected by the user
    const planPrice = getPlanPrice(currentPlan, billingCycle);

    // console.log("Proceeding with plan:", {
    // basePlan: selectedBasePlanId,
    // billingCycle,
    // teamMembers,
    // paymentMethods: selectedPaymentMethods,
    // campaigns: campaignsToSubmit, // Submit the actual number selected by the user
    // includedCampaigns: currentPlan ? currentPlan.includedCampaigns || 0 : 0, // Also track how many are included in the plan
    // additionalCampaignsCount: additionalCampaignsCount, // Track how many are additional beyond the plan
    // totalMonthlyCost,
    // totalAnnualCost:
    // billingCycle === "annual" ? totalAnnualCost : totalMonthlyCost * 12,
    // });
    // TODO: Step 3 - Call API POST /api/organizations/me/custom-plan
    // On success:
    history.push({
      pathname: "/OrgSignup", // Navigate to signup form
      state: {
        planType: "custom", // Or determine if it matches a standard plan
        planDetails: {
          // Pass configured details
          basePlanId: selectedBasePlanId,
          billingCycle,
          teamMembers,
          selectedPaymentMethods,
          campaigns: campaignsToSubmit, // Pass the actual number selected
          includedCampaigns: currentPlan
            ? currentPlan.includedCampaigns || 0
            : 0,
          additionalCampaignsCount: additionalCampaignsCount,
          totalMonthlyCost,
          totalAnnualCost:
            billingCycle === "annual" ? totalAnnualCost : totalMonthlyCost * 12,
        },
      },
    });
    // setIsLoading(false);
  };

  // --- Render ---
  // Use the language object passed via props, assuming it contains OrgPlanBuilder keys
  const lang =
    languageObj && languageObj.OrgPlanBuilder
      ? languageObj.OrgPlanBuilder
      : {
        // Fallback strings
        pageTitle: "Build Your Custom Plan",
        basePlanHeading: "1. Choose Your Foundation",
        basePlanDescription:
          "Select the core plan that best suits your initial needs. You can add features on top.",
        basePlanLabel: "Base Plan:",
        billingCycleHeading: "2. Select Billing Cycle",
        billingCycleDescription:
          "Choose between monthly or annual billing. Save with annual commitment.",
        billingCycleMonthly: "Monthly",
        billingCycleAnnual: "Annual",
        billingCycleSavingsText: "Save up to 20% with annual billing",
        teamMembersHeading: "3. Set Your Team Size",
        teamMembersDescription:
          "Select the number of team members who need access to the dashboard.",
        teamMembersLabel: `Team Members: ${teamMembers}`,
        paymentMethodsHeading: "4. Select Payment Options",
        paymentMethodsDescription:
          "Choose which payment methods you want to offer through CheckPlus.",
        paymentMethodsLabel: "Available Payment Methods:",
        campaignsHeading: "5. Select Campaign Limit", // Renamed from additionalCampaignsHeading
        campaignsDescription:
          "Select the maximum number of concurrent campaigns or donation items you need.", // Updated description
        campaignsLabel: "Campaigns:", // Renamed from additionalCampaignsLabel
        summaryTitle: "Summary",
        basePlanSummary: "Base Plan:",
        billingCycleSummary: "Billing Cycle:",
        teamMembersSummary: "Team Members:",
        paymentMethodsSummary: "Payment Methods:",
        campaignsSummary: "Campaigns:", // Renamed from additionalCampaignsSummary
        totalMonthlyCostLabel: "Total Monthly Cost:",
        totalAnnualCostLabel: "Total Annual Cost:",
        proceedButton: "Proceed to Sign Up",
        comparePlansButton: "Compare Full Plans",
        upgradeSuggestionPrefix: "Upgrade to",
        upgradeSuggestionSuffix: "and save",
        currencySymbol: "$",
        includedText: "(Included)",
        unlimitedText: "Unlimited",
        additionalMemberCostText: "Additional Member Cost:", // New key for clarity
        additionalPaymentMethodCostText: "Additional Method Cost:", // New key for clarity
      }; // Corrected closing brace and semicolon

  // Add a check for language object loading, though typically handled by parent/router
  if (!(languageObj && languageObj.OrgPlanBuilder)) {
    // Using fallback 'lang' object for now if main languageObj isn't ready
  }

  // Determine dynamic costs based on selected base plan
  const currentPlan = BASE_PLANS[selectedBasePlanId];
  const planPrice = getPlanPrice(currentPlan, billingCycle);
  const isPaidBase = currentPlan && planPrice > 0;
  const memberCost = isPaidBase
    ? FEATURE_COSTS.teamMember.paid
    : FEATURE_COSTS.teamMember.free;
  const methodCost = isPaidBase
    ? FEATURE_COSTS.paymentMethod.paid
    : FEATURE_COSTS.paymentMethod.free;
  const campaignCost = isPaidBase
    ? FEATURE_COSTS.campaign.paid
    : FEATURE_COSTS.campaign.free;

  // Create cost suffix strings
  const memberCostString = `(+$${memberCost}/member over included)`; // Clarify cost is for *additional* members

  // Calculate chargeable members and subtotal
  const includedMembersForPlan = currentPlan
    ? currentPlan.includedMembers || 0
    : 0;
  const chargeableMembers = Math.max(0, teamMembers - includedMembersForPlan);
  const memberSubtotal = chargeableMembers * memberCost;

  // Calculate additional payment methods and subtotal using grouped counting
  const additionalMethods = selectedPaymentMethods.filter(
    (m) => m !== "stripe",
  );
  const selectedGroups = [];
  const individualMethods = [];

  // Categorize selected methods into groups and individuals
  additionalMethods.forEach((methodId) => {
    const method = PAYMENT_METHODS.find((m) => m.id === methodId);
    if (method && method.group) {
      // Only add the group if it's not already in the array
      if (!selectedGroups.includes(method.group)) {
        selectedGroups.push(method.group);
      }
    } else if (method && !method.hidden) {
      individualMethods.push(methodId);
    }
  });

  // Calculate cost: count each group as 1 + individual methods
  const totalMethodCount = selectedGroups.length + individualMethods.length;

  // For Plus plan, one payment method group is included in the UI calculation
  let displayMethodCount = totalMethodCount;
  if (selectedBasePlanId === "Plus" && selectedGroups.length > 0) {
    displayMethodCount = Math.max(0, totalMethodCount - 1);
  }

  const methodSubtotal = displayMethodCount * methodCost;

  // Calculate savings percentage for annual billing
  const calculateSavingsPercentage = () => {
    if (!currentPlan) return 0;
    const monthlyPrice = currentPlan.monthlyPrice;
    const annualPrice = currentPlan.annualPrice;
    if (monthlyPrice === 0) return 0;
    return Math.round(((monthlyPrice - annualPrice) / monthlyPrice) * 100);
  };

  const savingsPercentage = calculateSavingsPercentage();

  // Determine campaign slider settings
  const isPremium = selectedBasePlanId === "Premium";
  const includedCampaignsForPlan = currentPlan
    ? currentPlan.includedCampaigns || 0
    : 0;
  const additionalCampaignsCount = Math.max(
    0,
    additionalCampaigns - includedCampaignsForPlan,
  );
  const campaignSubtotal = additionalCampaignsCount * campaignCost;
  const displayCampaigns = isPremium
    ? lang.unlimitedText
    : `${additionalCampaigns}`;
  const actualCampaignsSelected = isPremium ? "unlimited" : additionalCampaigns;

  // Format summary strings
  const teamSummaryText = `${teamMembers} (${includedMembersForPlan} included${chargeableMembers > 0 ? `, +${chargeableMembers} additional` : ""})`;
  const paymentMethodSummaryText = `${selectedPaymentMethods.length} (Stripe included${displayMethodCount > 0 ? `, +${displayMethodCount} additional` : ""})`;
  const campaignSummaryText = isPremium
    ? lang.unlimitedText
    : `${actualCampaignsSelected} (${includedCampaignsForPlan} included${additionalCampaignsCount > 0 ? `, +${additionalCampaignsCount} additional` : ""})`;
  const billingCycleSummaryText =
    billingCycle === "monthly"
      ? lang.billingCycleMonthly
      : lang.billingCycleAnnual;

  // --- Sub Components ---

  const ProgressIndicator = ({
    activeSection,
    completedSections,
    onSectionClick,
  }) => {
    const sections = [
      { id: "basePlan", name: lang.basePlanHeading, number: 1 },
      { id: "billingCycle", name: lang.billingCycleHeading, number: 2 },
      { id: "teamMembers", name: lang.teamMembersHeading, number: 3 },
      { id: "paymentMethods", name: lang.paymentMethodsHeading, number: 4 },
      { id: "campaigns", name: lang.campaignsHeading, number: 5 },
    ];

    return (
      <div className="OrgPlanBuilder-progressIndicator">
        {sections.map((section) => (
          <div
            key={section.id}
            className={`OrgPlanBuilder-progressStep ${
              activeSection === section.id ? "active" : ""
            } ${completedSections[section.id] ? "completed" : ""}`}
            onClick={() => onSectionClick(section.id)}
          >
            <div className="OrgPlanBuilder-stepCircle">
              {completedSections[section.id] ? (
                <span className="OrgPlanBuilder-checkmark">✓</span>
              ) : (
                <span>{section.number}</span>
              )}
            </div>
            <div className="OrgPlanBuilder-stepLabel">{section.name}</div>
            {section.id !== "campaigns" && (
              <div className="OrgPlanBuilder-stepConnector"></div>
            )}
          </div>
        ))}
      </div>
    );
  };

  ProgressIndicator.propTypes = {
    activeSection: PropTypes.string,
    completedSections: PropTypes.object.isRequired,
    onSectionClick: PropTypes.func.isRequired,
  };

  const CollapsibleSection = ({
    id,
    title,
    isExpanded,
    isCompleted,
    onToggle,
    onComplete,
    nextSectionId,
    children,
  }) => {
    return (
      <div
        className={`OrgPlanBuilder-featureSection ${isExpanded ? "expanded" : "collapsed"} ${isCompleted ? "completed" : ""}`}
      >
        <div
          className="OrgPlanBuilder-sectionHeader"
          onClick={() => onToggle(id)}
        >
          <h3>
            {isCompleted && (
              <span className="OrgPlanBuilder-sectionCheckmark">✓</span>
            )}
            {title}
            <span className="OrgPlanBuilder-expandIcon">
              {isExpanded ? "−" : "+"}
            </span>
          </h3>
        </div>

        {isExpanded && (
          <div className="OrgPlanBuilder-sectionContent">
            {children}

            {nextSectionId && (
              <button
                className="OrgPlanBuilder-continueButton"
                onClick={() => onComplete(id, nextSectionId)}
              >
                Continue to Next Step
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  CollapsibleSection.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    isExpanded: PropTypes.bool.isRequired,
    isCompleted: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    onComplete: PropTypes.func.isRequired,
    nextSectionId: PropTypes.string, // Can be null for the last section
    children: PropTypes.node.isRequired,
  };

  // --- Plan Comparison Modal Component ---
  const PlanComparisonModal = ({
    isOpen,
    onClose,
    plans,
    currentPlanId,
    recommendedPlanId,
    planCosts,
    billingCycle,
    teamMembers,
    selectedPaymentMethods,
    additionalCampaigns,
    onSelectPlan,
  }) => {
    if (!isOpen) return null;

    // Helper to calculate savings percentage for display in modal
    const calculateModalSavingsPercentage = (plan) => {
      if (!plan) return 0;
      const monthlyPrice = plan.monthlyPrice;
      const annualPrice = plan.annualPrice;
      if (monthlyPrice === 0 || annualPrice === 0) return 0; // Avoid division by zero or showing savings on free plans
      return Math.round(((monthlyPrice - annualPrice) / monthlyPrice) * 100);
    };

    return (
      <div className="OrgPlanBuilder-modalOverlay">
        <div className="OrgPlanBuilder-modalContent">
          <div className="OrgPlanBuilder-modalHeader">
            <h2>{lang.comparePlansTitle || "Compare Plans"}</h2>{" "}
            {/* Added fallback */}
            <button className="OrgPlanBuilder-closeButton" onClick={onClose}>
              ×
            </button>
          </div>

          <div className="OrgPlanBuilder-comparisonTableContainer">
            <table className="OrgPlanBuilder-comparisonTable">
              <thead>
                <tr>
                  <th className="OrgPlanBuilder-featureColumn">
                    {lang.featureColumnTitle || "Feature"}
                  </th>
                  {Object.entries(plans).map(([planId, plan]) => (
                    <th
                      key={planId}
                      className={`OrgPlanBuilder-planColumn ${planId === currentPlanId ? "current" : ""} ${planId === recommendedPlanId ? "recommended" : ""}`}
                    >
                      {plan.name}
                      {planId === recommendedPlanId && (
                        <span className="OrgPlanBuilder-recommendedBadge">
                          {lang.recommendedLabel || "Recommended"}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Price Row */}
                <tr>
                  <td>
                    {billingCycle === "monthly"
                      ? lang.monthlyPriceLabel || "Monthly Price"
                      : lang.annualPriceLabel || "Annual Price"}
                  </td>
                  {Object.entries(plans).map(([planId, plan]) => (
                    <td key={planId}>
                      {lang.currencySymbol}
                      {getPlanPrice(plan, billingCycle).toFixed(2)}
                      {billingCycle === "annual" &&
                        calculateModalSavingsPercentage(plan) > 0 && (
                        <div className="OrgPlanBuilder-savingsNote">
                          {calculateModalSavingsPercentage(plan)}%{" "}
                          {lang.savingsLabel || "Savings"}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>

                {/* Team Members Row */}
                <tr>
                  <td>{lang.teamMembersLabel || "Team Members"}</td>
                  {Object.entries(plans).map(([planId, plan]) => (
                    <td key={planId}>
                      {plan.includedMembers} {lang.includedLabel || "Included"}
                      <div className="OrgPlanBuilder-extraCostNote">
                        +{lang.currencySymbol}
                        {planId === "Free"
                          ? FEATURE_COSTS.teamMember.free
                          : FEATURE_COSTS.teamMember.paid}
                        /{lang.perExtraMemberLabel || "extra member"}
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Campaigns Row */}
                <tr>
                  <td>{lang.campaignsLabel || "Campaigns"}</td>
                  {Object.entries(plans).map(([planId, plan]) => (
                    <td key={planId}>
                      {planId === "Premium"
                        ? lang.unlimitedText
                        : `${plan.includedCampaigns} ${lang.includedLabel || "Included"}`}
                      {planId !== "Premium" && (
                        <div className="OrgPlanBuilder-extraCostNote">
                          +{lang.currencySymbol}
                          {planId === "Free"
                            ? FEATURE_COSTS.campaign.free
                            : FEATURE_COSTS.campaign.paid}
                          /{lang.perExtraCampaignLabel || "extra campaign"}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>

                {/* Payment Methods Row */}
                <tr>
                  <td>{lang.paymentMethodsLabel || "Payment Methods"}</td>
                  {Object.entries(plans).map(([planId, plan]) => {
                    const planRules = PLAN_PAYMENT_RULES[planId];
                    return (
                      <td key={planId}>
                        <div>
                          {planRules.includedMethods.map((methodId) => {
                            const method = PAYMENT_METHODS.find(
                              (m) => m.id === methodId,
                            );
                            return method ? (
                              <div
                                key={methodId}
                                className="OrgPlanBuilder-includedMethod"
                              >
                                ✓ {method.name}
                              </div>
                            ) : null;
                          })}
                        </div>

                        {planRules.allowedGroups.length > 0 && (
                          <div className="OrgPlanBuilder-allowedGroups">
                            <div className="OrgPlanBuilder-groupsLabel">
                              {lang.availableGroupsLabel || "Available Groups"}:
                            </div>
                            {planRules.allowedGroups.map((groupId) => {
                              const group = PAYMENT_METHOD_GROUPS[groupId];
                              return group && !group.hidden ? (
                                <div
                                  key={groupId}
                                  className="OrgPlanBuilder-allowedGroup"
                                >
                                  {group.name}
                                  {planId === "Plus" &&
                                    groupId === planRules.allowedGroups[0] && (
                                    <span className="OrgPlanBuilder-firstGroupFree">
                                      {" "}
                                        (
                                      {lang.firstGroupFreeLabel ||
                                          "1st group free"}
                                        )
                                    </span>
                                  )}
                                </div>
                              ) : null;
                            })}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>

                {/* Total Cost Row */}
                <tr className="OrgPlanBuilder-totalRow">
                  <td>
                    {lang.totalCostWithSelectionsLabel ||
                      "Total Cost (Your Selections)"}
                  </td>
                  {Object.entries(plans).map(([planId, plan]) => (
                    <td key={planId}>
                      {lang.currencySymbol}
                      {planCosts[planId]
                        ? planCosts[planId].totalCost.toFixed(2)
                        : "N/A"}
                      {planCosts[planId] && planCosts[planId].savings > 0 && (
                        <div className="OrgPlanBuilder-savingsAmount">
                          {lang.saveLabel || "Save"} {lang.currencySymbol}
                          {planCosts[planId].savings.toFixed(2)}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <div className="OrgPlanBuilder-modalFooter">
            {Object.entries(plans).map(([planId, plan]) => (
              <button
                key={planId}
                className={`OrgPlanBuilder-selectPlanButton ${planId === currentPlanId ? "current" : ""} ${planId === recommendedPlanId ? "recommended" : ""}`}
                onClick={() => {
                  onSelectPlan(planId);
                  onClose();
                }}
                disabled={planId === currentPlanId}
              >
                {planId === currentPlanId
                  ? lang.currentPlanLabel || "Current"
                  : lang.selectPlanLabel || "Select"}{" "}
                {plan.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  PlanComparisonModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    plans: PropTypes.object.isRequired,
    currentPlanId: PropTypes.string.isRequired,
    recommendedPlanId: PropTypes.string,
    planCosts: PropTypes.object.isRequired,
    billingCycle: PropTypes.string.isRequired,
    teamMembers: PropTypes.number.isRequired,
    selectedPaymentMethods: PropTypes.array.isRequired,
    additionalCampaigns: PropTypes.number.isRequired,
    onSelectPlan: PropTypes.func.isRequired,
    languageObj: PropTypes.object.isRequired, // Pass language object
  };

  // --- Affordable Plan Recommendation Component ---
  const AffordablePlanRecommendation = ({
    recommendation,
    onSelectPlan,
    onComparePlans,
  }) => {
    if (
      !recommendation ||
      recommendation.recommendedPlanId === recommendation.currentPlanId ||
      recommendation.potentialSavings <= 0
    ) {
      return null; // Don't show if no recommendation, already on recommended plan, or no savings
    }

    const recommendedPlan = BASE_PLANS[recommendation.recommendedPlanId];
    const currentPlan = BASE_PLANS[recommendation.currentPlanId];

    return (
      <div className="OrgPlanBuilder-recommendationContainer">
        <div className="OrgPlanBuilder-recommendationHeader">
          <span className="OrgPlanBuilder-recommendationIcon">💡</span>
          <h3>{lang.recommendationTitle || "Recommendation"}</h3>
        </div>

        <div className="OrgPlanBuilder-recommendationContent">
          <p className="OrgPlanBuilder-recommendationText">
            {lang.recommendationPrefix ||
              "Based on your selections, switching to"}{" "}
            <strong>{recommendedPlan.name}</strong>{" "}
            {lang.recommendationSuffix || "could save you"}{" "}
            <strong>
              {lang.currencySymbol}
              {recommendation.potentialSavings.toFixed(2)}
            </strong>{" "}
            {lang.perMonthLabel || "/month"}!
          </p>

          <div className="OrgPlanBuilder-planComparison">
            <div className="OrgPlanBuilder-currentPlan">
              <div className="OrgPlanBuilder-planLabel">
                {lang.currentPlanLabel || "Current"}
              </div>
              <div className="OrgPlanBuilder-planName">{currentPlan.name}</div>
              <div className="OrgPlanBuilder-planPrice">
                {lang.currencySymbol}
                {recommendation.allPlanCosts[
                  recommendation.currentPlanId
                ].totalCost.toFixed(2)}
              </div>
            </div>

            <div className="OrgPlanBuilder-arrow">→</div>

            <div className="OrgPlanBuilder-recommendedPlan">
              <div className="OrgPlanBuilder-planLabel">
                {lang.recommendedPlanLabel || "Recommended"}
              </div>
              <div className="OrgPlanBuilder-planName">
                {recommendedPlan.name}
              </div>
              <div className="OrgPlanBuilder-planPrice">
                {lang.currencySymbol}
                {recommendation.allPlanCosts[
                  recommendation.recommendedPlanId
                ].totalCost.toFixed(2)}
              </div>
            </div>
          </div>

          <div className="OrgPlanBuilder-recommendationActions">
            <button
              className="OrgPlanBuilder-selectRecommendedButton"
              onClick={() => onSelectPlan(recommendation.recommendedPlanId)}
            >
              {lang.switchToRecommendedPlanLabel ||
                "Switch to Recommended Plan"}
            </button>

            <button
              className="OrgPlanBuilder-compareAllButton"
              onClick={onComparePlans}
            >
              {lang.compareAllPlansLabel || "Compare All Plans"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  AffordablePlanRecommendation.propTypes = {
    recommendation: PropTypes.shape({
      recommendedPlanId: PropTypes.string.isRequired,
      currentPlanId: PropTypes.string.isRequired,
      potentialSavings: PropTypes.number.isRequired,
      allPlanCosts: PropTypes.object.isRequired,
    }),
    onSelectPlan: PropTypes.func.isRequired,
    onComparePlans: PropTypes.func.isRequired,
    languageObj: PropTypes.object.isRequired, // Pass language object
  };

  // --- Modal Handlers ---
  const handleOpenComparison = () => {
    setShowPlanComparison(true);
  };

  const handleCloseComparison = () => {
    setShowPlanComparison(false);
  };

  return (
    <div className="OrgPlanBuilder-view">
      {/* <OrgHeader /> */}
      <div className="OrgPlanBuilder-header">
        {/* Simple Logo Placeholder */}
        <div className="OrgPlanBuilder-logo"></div>
        {/* Add Back button? */}
      </div>

      <div className="OrgPlanBuilder-content">
        <h1>{lang.pageTitle}</h1>

        <ProgressIndicator
          activeSection={activeSection}
          completedSections={completedSections}
          onSectionClick={jumpToSection}
        />

        <div className="OrgPlanBuilder-configurator">
          {/* Base Plan */}
          <CollapsibleSection
            id="basePlan"
            title={lang.basePlanHeading}
            isExpanded={expandedSections.basePlan}
            isCompleted={completedSections.basePlan}
            onToggle={toggleSection}
            onComplete={completeSection}
            nextSectionId="billingCycle"
          >
            <p className="OrgPlanBuilder-featureDescription">
              {lang.basePlanDescription}
            </p>
            <label htmlFor="basePlanSelect">{lang.basePlanLabel}</label>
            <select
              id="basePlanSelect"
              value={selectedBasePlanId}
              onChange={handleBasePlanChange}
            >
              {Object.entries(BASE_PLANS).map(([id, plan]) => (
                <option key={id} value={id}>
                  {plan.name} ({lang.currencySymbol}
                  {getPlanPrice(plan, billingCycle)}/month)
                </option>
              ))}
            </select>
          </CollapsibleSection>

          {/* Billing Cycle */}
          <CollapsibleSection
            id="billingCycle"
            title={lang.billingCycleHeading}
            isExpanded={expandedSections.billingCycle}
            isCompleted={completedSections.billingCycle}
            onToggle={toggleSection}
            onComplete={completeSection}
            nextSectionId="teamMembers"
          >
            <p className="OrgPlanBuilder-featureDescription">
              {lang.billingCycleDescription}
            </p>
            <div className="OrgPlanBuilder-billingToggle">
              <div
                className={`OrgPlanBuilder-billingOption ${billingCycle === "monthly" ? "OrgPlanBuilder-billingOptionActive" : ""}`}
                onClick={() => handleBillingCycleChange("monthly")}
              >
                {lang.billingCycleMonthly}
              </div>
              <div
                className={`OrgPlanBuilder-billingOption ${billingCycle === "annual" ? "OrgPlanBuilder-billingOptionActive" : ""}`}
                onClick={() => handleBillingCycleChange("annual")}
              >
                {lang.billingCycleAnnual}
                {savingsPercentage > 0 && (
                  <span className="OrgPlanBuilder-savingsBadge">
                    Save {savingsPercentage}%
                  </span>
                )}
              </div>
            </div>
          </CollapsibleSection>

          {/* Team Members */}
          <CollapsibleSection
            id="teamMembers"
            title={lang.teamMembersHeading}
            isExpanded={expandedSections.teamMembers}
            isCompleted={completedSections.teamMembers}
            onToggle={toggleSection}
            onComplete={completeSection}
            nextSectionId="paymentMethods"
          >
            <span className="OrgPlanBuilder-costSuffix">
              {memberCostString}
            </span>
            <p className="OrgPlanBuilder-featureDescription">
              {lang.teamMembersDescription}
            </p>
            <label htmlFor="teamMembersSlider">{lang.teamMembersLabel}</label>
            <input
              type="range"
              id="teamMembersSlider"
              min={MIN_MEMBERS} // Ensure min is respected based on included
              max={MAX_MEMBERS}
              value={teamMembers}
              onChange={handleTeamMemberChange}
            />
            <span>{teamMembers}</span>
            <div className="OrgPlanBuilder-featureSubtotal">
              {lang.additionalMemberCostText} {lang.currencySymbol}
              {memberSubtotal.toFixed(2)}
            </div>
          </CollapsibleSection>

          {/* Payment Methods */}
          <CollapsibleSection
            id="paymentMethods"
            title={lang.paymentMethodsHeading}
            isExpanded={expandedSections.paymentMethods}
            isCompleted={completedSections.paymentMethods}
            onToggle={toggleSection}
            onComplete={completeSection}
            nextSectionId="campaigns"
          >
            <p className="OrgPlanBuilder-featureDescription">
              {lang.paymentMethodsDescription}
            </p>
            <fieldset>
              <legend>{lang.paymentMethodsLabel}</legend>

              {/* Individual payment methods without groups and not hidden */}
              {PAYMENT_METHODS.filter(
                (method) => !method.group && !method.hidden,
              ).map((method) => (
                <div
                  key={method.id}
                  className="OrgPlanBuilder-paymentMethodOption"
                >
                  <label>
                    <input
                      type="checkbox"
                      value={method.id}
                      checked={selectedPaymentMethods.includes(method.id)}
                      onChange={handlePaymentMethodChange}
                      disabled={isMethodIncludedInPlan(method.id)}
                    />
                    {method.name}
                    {isMethodIncludedInPlan(method.id) &&
                      ` ${lang.includedText}`}
                  </label>
                </div>
              ))}

              {/* Payment method groups */}
              {Object.entries(PAYMENT_METHOD_GROUPS)
                .filter(([groupId, group]) => !group.hidden)
                .map(([groupId, group]) => {
                  // Check if all methods in the group are selected
                  const allMethodsInGroupSelected = group.methods.every(
                    (methodId) => selectedPaymentMethods.includes(methodId),
                  );
                  // Check if any method in the group is included in the base plan (relevant for Premium)
                  const anyMethodIncluded = group.methods.some((methodId) =>
                    isMethodIncludedInPlan(methodId),
                  );

                  return (
                    <div
                      key={groupId}
                      className="OrgPlanBuilder-paymentMethodGroup"
                    >
                      {/* Render a single checkbox for the group */}
                      <div className="OrgPlanBuilder-paymentMethodOption OrgPlanBuilder-paymentMethodGroupHeader">
                        <label>
                          <input
                            type="checkbox"
                            value={groupId}
                            checked={allMethodsInGroupSelected}
                            onChange={(e) =>
                              handlePaymentMethodGroupChange(
                                groupId,
                                e.target.checked,
                              )
                            }
                            disabled={anyMethodIncluded} // Disable group checkbox if any method within it is included by the plan
                          />
                          {group.name}
                          {anyMethodIncluded && ` ${lang.includedText}`}{" "}
                          {/* Show included text if any method is included */}
                        </label>
                      </div>
                      {/* Optionally, still list individual methods within the group for clarity, but controlled by the group checkbox */}
                      {/* 
                    {group.methods.map(methodId => {
                      const method = PAYMENT_METHODS.find(m => m.id === methodId);
                      if (!method) return null;
                      return (
                        <div key={methodId} className="OrgPlanBuilder-paymentMethodOption OrgPlanBuilder-paymentMethodSubOption">
                          <label>
                            <input
                              type="checkbox"
                              value={methodId}
                              checked={selectedPaymentMethods.includes(methodId)}
                              onChange={handlePaymentMethodChange} // Keep individual handler if needed, but might be confusing
                              disabled={isMethodIncludedInPlan(methodId) || anyMethodIncluded} // Disable if group is included
                            />
                            {method.name}
                            {isMethodIncludedInPlan(methodId) && ` ${lang.includedText}`}
                          </label>
                        </div>
                      );
                    })}
                    */}
                    </div>
                  );
                })}
            </fieldset>
            <div className="OrgPlanBuilder-featureSubtotal">
              {lang.additionalPaymentMethodCostText} {lang.currencySymbol}
              {methodSubtotal.toFixed(2)}
            </div>
          </CollapsibleSection>

          {/* Campaigns */}
          <CollapsibleSection
            id="campaigns"
            title={lang.campaignsHeading}
            isExpanded={expandedSections.campaigns}
            isCompleted={completedSections.campaigns}
            onToggle={toggleSection}
            onComplete={completeSection}
            nextSectionId={null} // Last section
          >
            <p className="OrgPlanBuilder-featureDescription">
              {lang.campaignsDescription}
            </p>
            <label htmlFor="campaignsSlider">{lang.campaignsLabel}</label>
            <input
              type="range"
              id="campaignsSlider"
              min={MIN_CAMPAIGNS}
              max={MAX_CAMPAIGNS}
              value={additionalCampaigns}
              onChange={handleCampaignChange}
              disabled={isPremium}
            />
            <span>{displayCampaigns}</span>
            <div className="OrgPlanBuilder-featureSubtotal">
              {lang.additionalCampaignCostText} {lang.currencySymbol}
              {campaignSubtotal.toFixed(2)}
            </div>
          </CollapsibleSection>
        </div>

        {/* Affordable Plan Recommendation */}
        {affordablePlanRecommendation && (
          <AffordablePlanRecommendation
            recommendation={affordablePlanRecommendation}
            onSelectPlan={handleSelectSuggestedPlan} // Re-use existing handler
            onComparePlans={handleOpenComparison}
            languageObj={languageObj} // Pass language object
          />
        )}

        {/* Summary */}
        <div className="OrgPlanBuilder-summary">
          <h2>{lang.summaryTitle}</h2>
          <p>
            {lang.basePlanSummary} {currentPlan ? currentPlan.name : "None"}
          </p>
          <p>
            {lang.billingCycleSummary} {billingCycleSummaryText}
          </p>
          <p>
            {lang.teamMembersSummary} {teamSummaryText}
          </p>
          <p>
            {lang.paymentMethodsSummary} {paymentMethodSummaryText}
          </p>
          <p>
            {lang.campaignsSummary} {campaignSummaryText}
          </p>
          <div className="OrgPlanBuilder-totalCost">
            {billingCycle === "monthly" ? (
              <>
                {lang.totalMonthlyCostLabel} {lang.currencySymbol}
                {totalMonthlyCost.toFixed(2)}
              </>
            ) : (
              <>
                {lang.totalAnnualCostLabel} {lang.currencySymbol}
                {totalAnnualCost.toFixed(2)}
              </>
            )}
          </div>
          {/* Upgrade Suggestion */}
          {upgradeSuggestion && (
            <div className="OrgPlanBuilder-upgradeSuggestion">
              <p>
                {lang.upgradeSuggestionPrefix}{" "}
                <button
                  onClick={() =>
                    handleSelectSuggestedPlan(upgradeSuggestion.planId)
                  }
                >
                  {upgradeSuggestion.planName}
                </button>{" "}
                {lang.upgradeSuggestionSuffix} {lang.currencySymbol}
                {upgradeSuggestion.savings.toFixed(2)}/month!
              </p>
              {/* Additional details about the upgrade */}
              <p>
                {upgradeSuggestion.additionalIncludedMembers > 0 &&
                  `Includes ${upgradeSuggestion.additionalIncludedMembers} additional team members.`}
                {upgradeSuggestion.additionalIncludedCampaigns > 0 &&
                  `Includes ${upgradeSuggestion.additionalIncludedCampaigns} additional campaigns.`}
              </p>
            </div>
          )}
          <button
            className="OrgPlanBuilder-proceedButton"
            onClick={handleProceed}
          >
            {lang.proceedButton}
          </button>
          <button className="OrgPlanBuilder-comparePlansButton">
            {lang.comparePlansButton}
          </button>
        </div>
      </div>

      {/* Plan Comparison Modal */}
      {showPlanComparison && (
        <PlanComparisonModal
          isOpen={showPlanComparison}
          onClose={handleCloseComparison}
          plans={BASE_PLANS}
          currentPlanId={selectedBasePlanId}
          recommendedPlanId={
            affordablePlanRecommendation
              ? affordablePlanRecommendation.recommendedPlanId
              : null
          }
          planCosts={
            affordablePlanRecommendation
              ? affordablePlanRecommendation.allPlanCosts
              : {}
          }
          billingCycle={billingCycle}
          teamMembers={teamMembers}
          selectedPaymentMethods={selectedPaymentMethods}
          additionalCampaigns={additionalCampaigns}
          onSelectPlan={handleSelectSuggestedPlan} // Re-use existing handler
          languageObj={languageObj} // Pass language object
        />
      )}
    </div>
  );
};

// Define prop types for the component
OrgPlanBuilderComponent.propTypes = {
  languageObj: PropTypes.shape({
    OrgPlanBuilder: PropTypes.shape({
      pageTitle: PropTypes.string,
      basePlanHeading: PropTypes.string,
      basePlanDescription: PropTypes.string,
      basePlanLabel: PropTypes.string,
      billingCycleHeading: PropTypes.string,
      billingCycleDescription: PropTypes.string,
      billingCycleMonthly: PropTypes.string,
      billingCycleAnnual: PropTypes.string,
      billingCycleSavingsText: PropTypes.string,
      teamMembersHeading: PropTypes.string,
      teamMembersDescription: PropTypes.string,
      teamMembersLabel: PropTypes.string,
      paymentMethodsHeading: PropTypes.string,
      paymentMethodsDescription: PropTypes.string,
      paymentMethodsLabel: PropTypes.string,
      campaignsHeading: PropTypes.string,
      campaignsDescription: PropTypes.string,
      campaignsLabel: PropTypes.string,
      summaryTitle: PropTypes.string,
      basePlanSummary: PropTypes.string,
      billingCycleSummary: PropTypes.string,
      teamMembersSummary: PropTypes.string,
      paymentMethodsSummary: PropTypes.string,
      campaignsSummary: PropTypes.string,
      totalMonthlyCostLabel: PropTypes.string,
      totalAnnualCostLabel: PropTypes.string,
      proceedButton: PropTypes.string,
      comparePlansButton: PropTypes.string,
      upgradeSuggestionPrefix: PropTypes.string,
      upgradeSuggestionSuffix: PropTypes.string,
      currencySymbol: PropTypes.string,
      includedText: PropTypes.string,
      unlimitedText: PropTypes.string,
      additionalMemberCostText: PropTypes.string,
      additionalPaymentMethodCostText: PropTypes.string,
    }),
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  // languageObj is already defined within the shape above, remove duplicate
};

// Wrap withRouter to inject history as prop
const OrgPlanBuilder = withRouter(OrgPlanBuilderComponent);

export default OrgPlanBuilder;
